import { useMemo } from 'react'
import { Link } from 'react-router'
import { Badge } from '#app/components/ui/badge.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '#app/components/ui/table.tsx'
import { formatPrice } from '#app/utils/currency.ts'
import {
	type DbListing,
	EQUIPMENT_CATEGORIES,
	EQUIPMENT_TYPES,
	type MergedVendorPrice,
	mergeWithDbListings,
} from '../equipment-cost-estimator.data.ts'

function PriceCell({ cents }: { cents: number | null }) {
	if (cents == null)
		return <TableCell className="text-muted-foreground">—</TableCell>
	return (
		<TableCell className="font-semibold text-green-600 dark:text-green-400">
			{formatPrice(cents)}
		</TableCell>
	)
}

function VendorRow({ row }: { row: MergedVendorPrice }) {
	return (
		<TableRow className={row.isIllustrative ? 'bg-muted/30' : ''}>
			<TableCell className="font-medium">
				{row.isIllustrative ? (
					<span className="text-muted-foreground flex items-center gap-2">
						<Icon
							name="badge-dollar-sign"
							size="sm"
							className="shrink-0 text-amber-500"
						/>
						Typical DFW Rate
						<Badge variant="secondary" className="text-xs">
							Est.
						</Badge>
					</span>
				) : (
					<span className="flex items-center gap-1.5">
						<Icon
							name="building-2"
							size="sm"
							className="text-muted-foreground shrink-0"
						/>
						{row.vendorSlug ? (
							<Link
								to={`/businesses/${row.vendorSlug}`}
								className="text-primary hover:underline"
							>
								{row.vendorName}
							</Link>
						) : (
							row.vendorName
						)}
					</span>
				)}
			</TableCell>
			<PriceCell cents={row.dailyCents} />
			<PriceCell cents={row.weeklyCents} />
			<PriceCell cents={row.monthlyCents} />
			<TableCell>
				<span className="text-muted-foreground text-sm">{row.notes}</span>
			</TableCell>
		</TableRow>
	)
}

function RelatedTools({
	relatedSlugs,
}: {
	relatedSlugs: string[]
}) {
	const related = relatedSlugs
		.map((slug) => EQUIPMENT_TYPES.find((t) => t.slug === slug))
		.filter(Boolean)
	if (related.length === 0) return null

	return (
		<div className="mt-3 flex flex-wrap items-center gap-2">
			<span className="text-muted-foreground text-xs font-medium">
				Often rented together:
			</span>
			{related.map((eq) => (
				<a
					key={eq.slug}
					href={`#${eq.slug}`}
					className="text-primary inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs hover:bg-accent transition-colors"
				>
					<Icon name="link-2" className="size-3" />
					{eq.name}
				</a>
			))}
		</div>
	)
}

export function EquipmentPriceTables({
	dbListings,
	activeCategory,
}: {
	dbListings: DbListing[]
	activeCategory: string
}) {
	const mergedPrices = useMemo(
		() => mergeWithDbListings(dbListings),
		[dbListings],
	)

	const category = EQUIPMENT_CATEGORIES.find((c) => c.slug === activeCategory)
	if (!category) return null

	const typesInCategory = EQUIPMENT_TYPES.filter(
		(t) => t.categorySlug === category.slug,
	)

	// Count real vendor rows for this category
	const realVendorCount = typesInCategory.reduce((acc, eqType) => {
		const rows = mergedPrices.get(eqType.slug) ?? []
		return acc + rows.filter((r) => !r.isIllustrative).length
	}, 0)

	return (
		<section id={`category-${category.slug}`}>
			<div className="flex items-center gap-3">
				<div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
					<Icon
						name={category.icon as IconName}
						size="md"
						className="text-primary"
					/>
				</div>
				<div>
					<Badge variant="secondary" className="mb-1">
						Price Comparison
					</Badge>
					<h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
						{category.seoHeading}
					</h2>
				</div>
			</div>
			<p className="text-muted-foreground mt-2">
				Compare current {category.name.toLowerCase()} rental rates from
				Dallas-Fort Worth vendors.
				{realVendorCount > 0
					? ` ${realVendorCount} vendor listing${realVendorCount !== 1 ? 's' : ''} available.`
					: ' Typical DFW rates shown for reference.'}
			</p>

			<div className="mt-8 space-y-8">
				{typesInCategory.map((eqType) => {
					const rows = mergedPrices.get(eqType.slug) ?? []
					const realRows = rows.filter((r) => !r.isIllustrative)

					return (
						<Card
							key={eqType.slug}
							id={eqType.slug}
							className="transition-shadow hover:shadow-md"
						>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle className="flex items-center gap-2">
										<Icon
											name={category.icon as IconName}
											size="sm"
											className="text-muted-foreground"
										/>
										{eqType.name}
									</CardTitle>
									{realRows.length > 0 && (
										<Badge className="bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-300">
											{realRows.length} vendor
											{realRows.length !== 1 ? 's' : ''}
										</Badge>
									)}
								</div>
								<p className="text-muted-foreground text-sm">
									{eqType.description}
								</p>
							</CardHeader>
							<CardContent>
								{rows.length === 0 ? (
									<div className="flex flex-col items-center py-8 text-center">
										<div className="bg-muted mb-4 flex size-12 items-center justify-center rounded-full">
											<Icon
												name={category.icon as IconName}
												size="md"
												className="text-muted-foreground"
											/>
										</div>
										<p className="text-muted-foreground">
											No vendors currently listed for{' '}
											{eqType.name.toLowerCase()} rentals. Check back soon or{' '}
											<Link
												to="/rentals"
												className="text-primary hover:underline"
											>
												browse all rentals
											</Link>
											.
										</p>
									</div>
								) : (
									<div className="overflow-hidden rounded-lg border">
										<div className="overflow-x-auto">
											<Table>
												<TableHeader>
													<TableRow className="bg-muted/50">
														<TableHead className="min-w-[160px]">
															Vendor
														</TableHead>
														<TableHead>
															<span className="flex items-center gap-1">
																<Icon
																	name="calendar"
																	size="sm"
																	className="text-muted-foreground"
																/>
																Daily
															</span>
														</TableHead>
														<TableHead>
															<span className="flex items-center gap-1">
																<Icon
																	name="calendar"
																	size="sm"
																	className="text-muted-foreground"
																/>
																Weekly
															</span>
														</TableHead>
														<TableHead>
															<span className="flex items-center gap-1">
																<Icon
																	name="calendar"
																	size="sm"
																	className="text-muted-foreground"
																/>
																Monthly
															</span>
														</TableHead>
														<TableHead>Notes</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{rows.map((row, idx) => (
														<VendorRow key={idx} row={row} />
													))}
												</TableBody>
											</Table>
										</div>
									</div>
								)}

								{eqType.relatedSlugs && eqType.relatedSlugs.length > 0 && (
									<RelatedTools relatedSlugs={eqType.relatedSlugs} />
								)}
							</CardContent>
						</Card>
					)
				})}
			</div>
		</section>
	)
}
